// ----------------------------------------------------------------------
// <copyright file="Izenda.GaugeFieldPropsSchema.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import { CHART_TYPE } from 'logic/data/Izenda.ChartDefaultConfig';
import CONSTANTS from 'logic/data/Izenda.ReportPartGaugeConstants';
import commonFieldPropsSchema from 'modules/ReportDesigner/Fields/PropertiesBuilder/Izenda.ReportPartFieldPropsSchema';
import { get, concat, remove, set, getOr, isFunction } from 'lodash/fp';

const ContainerName = CONSTANTS.ContainerName;
const UNUSED_PROPS_COMMON = [
  'fields.dataFormattings.fields.fontFormat',
  'fields.dataFormattings.fields.fontStyle',
  'fields.dataFormattings.fields.alignment',
  'fields.dataFormattings.fields.verticalAlignment',
  'fields.dataFormattings.fields.subTotal',
  'fields.dataFormattings.fields.grandTotal',
  'fields.dataFormattings.fields.width',
  'fields.dataFormattings.fields.columnWordWrap',
  'fields.headerFormating'
];

let UNUSED_PROPS_BY_TYPE = {};

// Solid gauge
UNUSED_PROPS_BY_TYPE[CHART_TYPE.SolidGauge] = {};

// Solid gauge -  values
UNUSED_PROPS_BY_TYPE[CHART_TYPE.SolidGauge][ContainerName.VALUES] = [
  'fields.dataFormattings.fields.customURL',
  'fields.dataFormattings.fields.embeddedJavascript',
  'fields.drillDown.fields.subReport'
];

// Solid gauge -  threshold
UNUSED_PROPS_BY_TYPE[CHART_TYPE.SolidGauge][ContainerName.THRESHOLD] = [
  'fields.dataFormattings.fields.sort',
  'fields.dataFormattings.fields.color',
  'fields.dataFormattings.fields.alternativeText',
  'fields.dataFormattings.fields.customURL',
  'fields.dataFormattings.fields.embeddedJavascript',

  'fields.drillDown.fields.subReport'
];

// Linear gauge (same as solid gauge)
UNUSED_PROPS_BY_TYPE[CHART_TYPE.LinearGauge] = UNUSED_PROPS_BY_TYPE[CHART_TYPE.SolidGauge];

// Simple gauge (same as solid/linear gauge)
UNUSED_PROPS_BY_TYPE[CHART_TYPE.SimpleGauge] = UNUSED_PROPS_BY_TYPE[CHART_TYPE.SolidGauge];
// Simple gauge -supplementary KPI (same as values)
UNUSED_PROPS_BY_TYPE[CHART_TYPE.SimpleGauge][ContainerName.SUP_KPI] =
  UNUSED_PROPS_BY_TYPE[CHART_TYPE.SimpleGauge][ContainerName.VALUES];

export default function(editorComp, reportDesignerModel, fieldStore, itemProps) {
  const configField = itemProps.configField || {};
  const configReportPart = reportDesignerModel.configReportPart;
  const reportPartContent = configReportPart.reportPartContent;
  const fieldContainerDetails = itemProps.fieldContainerDetails || {};

  const chartType = reportPartContent.chartType;
  const containerName = fieldContainerDetails.name || '';

  let chartFieldPropsSchema = commonFieldPropsSchema.apply(null, arguments);

  chartFieldPropsSchema.UPDATE_DATA_BY_KEY_CHANGE = concat(chartFieldPropsSchema.UPDATE_DATA_BY_KEY_CHANGE, [
    'fieldNameAlias'
  ]);

  let mappingSource = chartFieldPropsSchema.mappingSource;
  let mappingProps = chartFieldPropsSchema.mappingProps;
  mappingProps.stypeOptions = remove({ value: 'embedded' }, mappingProps.stypeOptions);

  // update schema
  chartFieldPropsSchema.definedSchema = updateSchemaToChart(chartFieldPropsSchema.definedSchema);

  // data formatting > color: show text color only
  showTextColor(chartFieldPropsSchema.definedSchema);

  // update action for function-properties
  updateChangeFunction(chartFieldPropsSchema);

  return chartFieldPropsSchema;

  function updateChangeFunction(schema) {
    const preBeforeChangeFunction = get('mappingProps.beforeChangeFunction', schema);
    // update for before report on change
    $izenda.extend(true, schema, {
      mappingProps: {
        beforeChangeFunction: function(newVal) {
          if (newVal && newVal.value) {
            return isFunction(preBeforeChangeFunction)
              ? preBeforeChangeFunction.apply(this, arguments)
              : Promise.resolve();
          }

          return (
            this.props.confirmMessage &&
            this.props.confirmMessage('error', CONSTANTS.Message.M05).then(() => {
              return Promise.reject(null);
            })
          );
        }
      }
    });
  }

  /**
   * Remove unsed-properties
   * @param  {object} fieldSchema
   * @return {object}
   */
  function updateSchemaToChart(fieldSchema) {
    let updatedFieldSchema = fieldSchema;

    // remove common-unused-fields
    UNUSED_PROPS_COMMON.forEach(pathItem => {
      updatedFieldSchema = set([pathItem, 'isHidden'].join('.'), true, updatedFieldSchema);
    });

    // remove unused-fields based on chart type & cotainer name
    getOr([], [chartType, containerName].join('.'), UNUSED_PROPS_BY_TYPE).forEach(pathItem => {
      updatedFieldSchema = set([pathItem, 'isHidden'].join('.'), true, updatedFieldSchema);
    });

    return updatedFieldSchema;
  }

  function showTextColor(definedSchema) {
    const colorDef = get('fields.dataFormattings.fields.color', definedSchema);

    if (colorDef) {
      $izenda.extend(true, colorDef, {
        staticProps: {
          showTextColor: true,
          showCellColor: false
        }
      });
    }
  }
}



// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/Fields/ReportBody/ReportPartType/ReportPartGauge/Izenda.GaugeFieldPropsSchema.js